// src/components/Dashboard/PlaidDataSidebar.js - Optimized for Scrolling
import React, { useState, useEffect } from 'react';
import { Card, Table, Badge, Accordion, Spinner, Button, Alert } from 'react-bootstrap';
import PlaidLinkButton from '../Plaid/PlaidLinkButton';
import api from '../../services/api';
import logger from '../../utils/logger';

/**
 * Modern component for displaying Plaid account and transaction data in a sidebar
 * with proper user validation, contemporary styling, and optimized scrolling
 */
const PlaidDataSidebar = ({
	userData,
	isVisible,
	isLoading = false,
	onRefresh,
	onPlaidSuccess,
	userId
}) => {
	const [activeKey, setActiveKey] = useState(['0', '1']);
	const [dataIssueDetected, setDataIssueDetected] = useState(false);
	const [isDuplicateData, setIsDuplicateData] = useState(false);
	const [isReconnecting, setIsReconnecting] = useState(false);

	useEffect(() => {
		if (userData && userData.transactions && userData.transactions.length > 0) {
			logger.info("Transaction data example:", userData.transactions[0]);
		}
	}, [userData]);

	// Check for data issues and validate user data
	useEffect(() => {
		if (userData && userData.accounts) {
			if (userData.userId && userId && userData.userId !== userId) {
				logger.error('User data mismatch detected', {
					expected: userId,
					found: userData.userId
				});
				setDataIssueDetected(true);
				return;
			}
			detectDataIssues(userData);
		}
	}, [userData, userId]);

	const detectDataIssues = (data) => {
		if (!data || !data.accounts || !Array.isArray(data.accounts)) {
			return;
		}

		const accountMap = new Map();
		let hasDuplicates = false;

		data.accounts.forEach(account => {
			const key = `${account.name}-${account.balance}-${account.type}`;
			if (accountMap.has(key)) {
				hasDuplicates = true;
			} else {
				accountMap.set(key, account);
			}
		});

		setIsDuplicateData(hasDuplicates);
		setDataIssueDetected(hasDuplicates);
	};

	const handleReconnect = async () => {
		try {
			setIsReconnecting(true);
			logger.info(`Initiating bank reconnection process for user ${userId}`);
			await api.post('/plaid/reconnect');
			logger.info(`Reconnection preparation successful for user ${userId}, ready for Plaid Link`);
		} catch (error) {
			logger.error(`Error preparing for reconnection for user ${userId}:`, error);
			setIsReconnecting(false);
		}
	};

	const handleReconnectSuccess = (linkData) => {
		setIsReconnecting(false);
		if (onPlaidSuccess) {
			onPlaidSuccess(linkData);
		}
	};

	const handleReconnectExit = (err) => {
		setIsReconnecting(false);
		if (err) {
			logger.error(`Error during reconnection for user ${userId}:`, err);
		}
	};

	if (!isVisible) return null;

	const handleAccordionToggle = (key) => {
		if (activeKey.includes(key)) {
			setActiveKey(activeKey.filter(k => k !== key));
		} else {
			setActiveKey([...activeKey, key]);
		}
	};

	const formatCurrency = (amount) => {
		try {
			return new Intl.NumberFormat('en-US', {
				style: 'currency',
				currency: 'USD'
			}).format(amount);
		} catch (error) {
			logger.error('Error formatting currency:', error);
			return `$${amount}`;
		}
	};

	const formatDate = (dateString) => {
		try {
			return new Date(dateString).toLocaleDateString();
		} catch (error) {
			logger.error('Error formatting date:', error);
			return dateString;
		}
	};

	const calculateTotals = () => {
		if (!userData || !userData.accounts || !Array.isArray(userData.accounts)) {
			return { total: 0, assets: 0, liabilities: 0 };
		}

		return userData.accounts.reduce((totals, account) => {
			const isCreditCard = account.type === 'Credit Card';

			if (isCreditCard) {
				totals.liabilities += Math.abs(account.balance);
				totals.total += account.balance;
			} else {
				totals.assets += account.balance;
				totals.total += account.balance;
			}

			return totals;
		}, { total: 0, assets: 0, liabilities: 0 });
	};

	const totals = calculateTotals();

	if (isLoading) {
		return (
			<div className="modern-loading-container">
				<div className="modern-spinner">
					<div className="spinner-ring"></div>
					<div className="spinner-ring"></div>
					<div className="spinner-ring"></div>
				</div>
				<p className="loading-text">Loading your financial data...</p>
			</div>
		);
	}

	if (!userData || !userData.accounts || userData.accounts.length === 0) {
		return (
			<div className="modern-empty-state">
				<div className="empty-state-icon">
					<i className="bi bi-bank"></i>
				</div>
				<h5>No Financial Data Available</h5>
				<p>Connect your bank account to view your financial data here.</p>
				<div className="empty-state-decoration"></div>
			</div>
		);
	}

	return (
		<div className="modern-plaid-data-sidebar">
			{/* Fixed Header - Non-scrollable */}
			<div className="modern-sidebar-header">
				<div className="header-content">
					<div className="header-icon connected">
						<i className="bi bi-bank"></i>
					</div>
					<div className="header-text">
						<h5>Your Financial Data</h5>
						<span className="header-subtitle">Connected to {userData.institution || 'Your Bank'}</span>
					</div>
				</div>
				<div className="connection-badge">
					<div className="connection-indicator"></div>
					<span>Live</span>
				</div>
			</div>

			{/* Scrollable Body Content */}
			<div className="modern-sidebar-body">
				{/* User validation warning */}
				{userData.userId && userId && userData.userId !== userId && (
					<div className="modern-alert error">
						<div className="alert-icon">
							<i className="bi bi-exclamation-triangle-fill"></i>
						</div>
						<div className="alert-content">
							<strong>Security Alert:</strong> Data ownership mismatch detected
						</div>
						<button className="modern-btn modern-btn-xs modern-btn-danger alert-action" onClick={handleReconnect}>
							Reset Connection
						</button>
					</div>
				)}

				{/* Data Issue Alert */}
				{dataIssueDetected && (
					<div className="modern-alert warning">
						<div className="alert-icon">
							<i className="bi bi-exclamation-triangle-fill"></i>
						</div>
						<div className="alert-content">
							{isDuplicateData ? 'Duplicate account data detected.' : 'Issues detected with your financial data.'}
						</div>
						<div className="alert-action">
							{isReconnecting ? (
								<PlaidLinkButton
									onSuccess={handleReconnectSuccess}
									onExit={handleReconnectExit}
									buttonText="Reconnect Bank"
									className="modern-btn modern-btn-sm modern-btn-warning"
								/>
							) : (
								<button
									className="modern-btn modern-btn-sm modern-btn-warning"
									onClick={handleReconnect}
								>
									Reconnect Bank
								</button>
							)}
						</div>
					</div>
				)}

				{/* Modern Financial Summary - Fixed height to prevent jumping */}
				<div className="financial-summary-card">
					<div className="summary-header">
						<h6>Financial Overview</h6>
						<div className="summary-icon">
							<i className="bi bi-graph-up"></i>
						</div>
					</div>

					<div className="summary-metrics">
						<div className="metric-item">
							<span className="metric-label">Total Assets</span>
							<span className="metric-value positive">
								{formatCurrency(totals.assets)}
							</span>
						</div>

						<div className="metric-item">
							<span className="metric-label">Total Liabilities</span>
							<span className="metric-value negative">
								{formatCurrency(totals.liabilities)}
							</span>
						</div>

						<div className="metric-divider"></div>

						<div className="metric-item total">
							<span className="metric-label">Net Worth</span>
							<span className={`metric-value ${totals.total >= 0 ? 'positive' : 'negative'}`}>
								{formatCurrency(totals.total)}
							</span>
						</div>
					</div>

					<div className="summary-footer">
						<span className="last-updated">
							Updated {formatDate(userData.lastUpdated || new Date())}
						</span>
					</div>
				</div>

				{/* Modern Expandable Sections - Original structure with CSS limits */}
				<div className="modern-expandable-sections">
					{/* Accounts Section */}
					<div className="expandable-section">
						<button
							className={`section-header ${activeKey.includes('0') ? 'expanded' : ''}`}
							onClick={() => handleAccordionToggle('0')}
						>
							<div className="section-title">
								<div className="section-icon">
									<i className="bi bi-wallet2"></i>
								</div>
								<span>Accounts</span>
								<div className="section-count">
									{userData.accounts?.length || 0}
								</div>
							</div>
							<div className="expand-icon">
								<i className={`bi bi-chevron-${activeKey.includes('0') ? 'up' : 'down'}`}></i>
							</div>
						</button>

						{activeKey.includes('0') && (
							<div className="section-content">
								<div className="accounts-grid">
									{userData.accounts?.map(account => (
										<div key={account.accountId} className="account-item">
											<div className="account-header">
												<h6 className="account-name">{account.name}</h6>
												<span className="account-type">{account.type}</span>
											</div>
											<div className={`account-balance ${account.balance < 0 ? 'negative' : 'positive'}`}>
												{formatCurrency(Math.abs(account.balance))}
												{account.balance < 0 && (
													<span className="balance-note">(owed)</span>
												)}
											</div>
										</div>
									))}
								</div>
							</div>
						)}
					</div>

					{/* Transactions Section - Using existing CSS structure */}
					<div className="expandable-section">
						<button
							className={`section-header ${activeKey.includes('1') ? 'expanded' : ''}`}
							onClick={() => handleAccordionToggle('1')}
						>
							<div className="section-title">
								<div className="section-icon">
									<i className="bi bi-receipt"></i>
								</div>
								<span>Recent Transactions</span>
								<div className="section-count">
									{userData.transactions?.length || 0}
								</div>
							</div>
							<div className="expand-icon">
								<i className={`bi bi-chevron-${activeKey.includes('1') ? 'up' : 'down'}`}></i>
							</div>
						</button>

						{activeKey.includes('1') && (
							<div className="section-content">
								<div className="transactions-list-container">
									<div className="transactions-list">
										{userData.transactions?.slice(0, 15).map(transaction => (
											<div key={transaction.transactionId} className="transaction-item">
												<div className="transaction-info">
													<div className="transaction-header">
														<span className="transaction-date">
															{formatDate(transaction.date)}
														</span>
														<span className={`transaction-amount ${transaction.amount < 0 ? 'negative' : 'positive'}`}>
															{formatCurrency(Math.abs(transaction.amount))}
														</span>
													</div>
													<h6 className="transaction-description">
														{transaction.description}
													</h6>
													<div className="transaction-meta">
														<span className="transaction-category">
															{(() => {
																if (!transaction.category) return 'Uncategorized';
																if (!Array.isArray(transaction.category)) {
																	return transaction.category || 'Uncategorized';
																}
																return transaction.category.length > 0 ? transaction.category[0] : 'Uncategorized';
															})()}
														</span>
														<span className="transaction-type">
															{transaction.amount < 0 ? 'Debit' : 'Credit'}
														</span>
													</div>
												</div>
											</div>
										))}
									</div>

									{userData.transactions?.length > 15 && (
										<div className="transactions-footer">
											<span>Showing 15 of {userData.transactions.length} transactions</span>
										</div>
									)}
								</div>
							</div>
						)}
					</div>
				</div>
			</div>

			{/* Fixed Footer - Non-scrollable */}
			<div className="modern-sidebar-footer">
				{/* Modern Action Buttons */}
				<div className="modern-actions">
					<button
						className="modern-btn modern-btn-primary modern-btn-full"
						onClick={onRefresh}
						disabled={isLoading}
					>
						<i className="bi bi-arrow-clockwise me-2"></i>
						Refresh Data
					</button>

					{!isReconnecting ? (
						<button
							className="modern-btn modern-btn-secondary modern-btn-full"
							onClick={handleReconnect}
						>
							<i className="bi bi-bank me-2"></i>
							Reconnect Bank
						</button>
					) : (
						<div className="reconnect-container">
							<PlaidLinkButton
								onSuccess={handleReconnectSuccess}
								onExit={handleReconnectExit}
								buttonText="Complete Reconnection"
								className="modern-btn modern-btn-warning modern-btn-full"
							/>
						</div>
					)}
				</div>

				<div className="security-badge">
					<i className="bi bi-shield-check me-2"></i>
					<span>
						Data verification: {userData.userId === userId ? 'Verified' : 'Mismatch Detected'}
					</span>
				</div>
			</div>
		</div>
	);
};

export default PlaidDataSidebar;